import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import PieChartOutlineOutlinedIcon from '@mui/icons-material/PieChartOutlineOutlined';
import {
  Badge,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useInvestorScope } from '../store/selectors';
import { investorColors } from '../ui/theme';
import PrototypeBar from './PrototypeBar';

const DESKTOP_NAV = [
  { to: '/investor', label: 'Home', exact: true },
  { to: '/investor/portfolio', label: 'Portfolio' },
  { to: '/investor/transactions', label: 'Activity' },
  { to: '/investor/payments', label: 'Payments' },
  { to: '/investor/tax', label: 'Tax' },
  { to: '/investor/documents', label: 'Documents' },
];

const MOBILE_NAV = [
  { to: '/investor', label: 'Home', icon: <HomeOutlinedIcon /> },
  { to: '/investor/portfolio', label: 'Portfolio', icon: <PieChartOutlineOutlinedIcon /> },
  { to: '/investor/payments', label: 'Payments', icon: <PaymentsOutlinedIcon /> },
  { to: '/investor/tasks', label: 'Tasks', icon: <ChecklistOutlinedIcon /> },
  { to: '/investor/profile', label: 'Profile', icon: <AccountCircleOutlinedIcon /> },
];

export default function InvestorShell({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const scope = useInvestorScope();
  const openTasks = scope.tasks.filter((task) => task.status === 'OPEN').length;
  const active = (to: string, exact?: boolean) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);
  const mobileValue = MOBILE_NAV.findIndex((item, index) =>
    index === 0 ? location.pathname === item.to : location.pathname.startsWith(item.to),
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: { xs: 9, md: 4 } }}>
      <PrototypeBar />
      <Box component="header" sx={{ bgcolor: investorColors.header, color: '#fff' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={3}
          sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2, md: 3 }, py: 1.5 }}
        >
          <Typography
            component={RouterLink}
            to="/investor"
            sx={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: '1.35rem',
              color: '#fff',
              textDecoration: 'none',
            }}
          >
            {scope.tenant.shortName}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ display: { xs: 'none', md: 'flex' }, flex: 1 }}
            component="nav"
            aria-label="Investor navigation"
          >
            {DESKTOP_NAV.map((item) => (
              <Button
                key={item.to}
                component={RouterLink}
                to={item.to}
                sx={{
                  color: active(item.to, item.exact) ? '#fff' : 'rgba(255,255,255,0.72)',
                  fontWeight: active(item.to, item.exact) ? 700 : 500,
                  borderBottom: active(item.to, item.exact)
                    ? '2px solid #c2d4bd'
                    : '2px solid transparent',
                  borderRadius: 0,
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>
          <Box sx={{ flex: { xs: 1, md: 0 } }} />
          <Tooltip title="Messages">
            <IconButton
              onClick={() => navigate('/investor/messages')}
              sx={{ color: '#fff' }}
              aria-label="Messages"
            >
              <MailOutlineIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Tasks">
            <IconButton
              onClick={() => navigate('/investor/tasks')}
              sx={{ color: '#fff' }}
              aria-label={`${openTasks} open tasks`}
            >
              <Badge badgeContent={openTasks} color="warning">
                <ChecklistOutlinedIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Tooltip title="Profile and entities">
            <IconButton
              onClick={() => navigate('/investor/profile')}
              sx={{ color: '#fff', display: { xs: 'none', md: 'inline-flex' } }}
              aria-label="Profile"
            >
              <AccountCircleOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
      <Box
        component="main"
        sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 2.5, md: 4 } }}
      >
        {children}
      </Box>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          display: { xs: 'block', md: 'none' },
          zIndex: 20,
        }}
      >
        <BottomNavigation
          showLabels
          value={mobileValue}
          onChange={(_event, index) => navigate(MOBILE_NAV[index].to)}
        >
          {MOBILE_NAV.map((item) => (
            <BottomNavigationAction
              key={item.to}
              label={item.label}
              icon={
                item.label === 'Tasks' ? (
                  <Badge badgeContent={openTasks} color="warning">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )
              }
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
